import { useState } from "react";
import { Link } from "react-router-dom";
import { submitFeedback } from "../services/teaserApi";
import { track } from "../utils/analytics";

// Asked straight after the reading, while it is fresh. One tap is the whole
// ask; the words and the survey are offered only after it.
export const RATINGS = [
  { value: 1, label: "Not at all" },
  { value: 2, label: "A little" },
  { value: 3, label: "Somewhat" },
  { value: 4, label: "Mostly" },
  { value: 5, label: "Very much" },
];

const STAR = "M12 2.8l2.84 5.76 6.36.92-4.6 4.48 1.08 6.33L12 17.3l-5.68 2.99 1.08-6.33-4.6-4.48 6.36-.92z";

/**
 * Five boxes, each showing its stars (one for "Not at all" up to five for
 * "Very much") above the words, so the scale reads at a glance as it does
 * on review sites. A radio group, so it works with a keyboard and a screen
 * reader too.
 */
export function StarRating({ value, onChange, label }) {
  return (
    <div className="grid grid-cols-5 gap-1.5" role="radiogroup" aria-label={label}>
      {RATINGS.map(({ value: v, label: meaning }) => {
        const selected = value === v;
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`${v} star${v > 1 ? "s" : ""}: ${meaning}`}
            onClick={() => onChange(v)}
            className={`flex flex-col items-center gap-1.5 rounded-xl border px-1 py-2.5 text-[11px] font-medium leading-tight transition ${
              selected ? "border-orange-500 bg-orange-50 text-gray-900 ring-2 ring-orange-200"
                : "border-gray-200 bg-white text-gray-700 hover:border-orange-300"
            }`}
          >
            <span className="flex flex-wrap justify-center gap-px" aria-hidden="true">
              {Array.from({ length: v }, (_, i) => (
                <svg key={i} viewBox="0 0 24 24" className="h-3 w-3">
                  <path d={STAR} fill="#f59e0b" stroke="#f59e0b" strokeWidth="1.5" strokeLinejoin="round" />
                </svg>
              ))}
            </span>
            {meaning}
          </button>
        );
      })}
    </div>
  );
}

export default function PreviewFeedback({ requestId }) {
  const [rating, setRating] = useState(null);
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);

  function rate(value) {
    setRating(value);
    track("feedback_rating_given", { requestId, rating: String(value) });
    // Saved at once, so the rating counts even if the reader writes nothing.
    submitFeedback(requestId, { kind: "quick", rating: value }).catch(() => {});
  }

  function send(event) {
    event.preventDefault();
    if (text.trim()) {
      submitFeedback(requestId, { kind: "quick", rating, text: text.trim() }).catch(() => {});
    }
    setSent(true);
  }

  return (
    <section aria-labelledby="feedback-title" className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3">
      <h3 id="feedback-title" className="text-center text-sm font-semibold text-gray-900">
        How well does this preview reflect you?
      </h3>
      <StarRating value={rating} onChange={rate} label="How well does this preview reflect you?" />

      {rating && !sent ? (
        <form onSubmit={send} className="space-y-2">
          <label htmlFor="feedback-text" className="block text-sm text-gray-700">
            Thank you. What felt most true, or most off? <span className="text-gray-500">(optional)</span>
          </label>
          <textarea
            id="feedback-text"
            rows={3}
            maxLength={1000}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full rounded-xl border border-gray-300 p-3 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200"
          />
          <div className="flex items-center justify-between gap-3">
            <Link to={`/feedback?requestId=${encodeURIComponent(requestId)}`}
              className="text-sm font-medium text-orange-600 underline">
              Answer 5 more questions
            </Link>
            <button type="submit" className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white">
              Send
            </button>
          </div>
        </form>
      ) : null}

      {sent ? (
        <p className="text-sm text-gray-700" role="status">
          Thank you, this helps us make AyuPath better.{" "}
          <Link to={`/feedback?requestId=${encodeURIComponent(requestId)}`}
            className="font-medium text-orange-600 underline">
            Tell us a little more
          </Link>
        </p>
      ) : null}
    </section>
  );
}
